import React, { useEffect, useState } from 'react';

import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { Tab, Tabs } from 'react-bootstrap';

// Internal imports: eslint-plugin-simple-import-sort — group 3 by first identifier (A→z).
import { blackberry, purple1, white } from 'lib/css/colors';
import Container from 'components/shared/Container';
import {
  L4_CATEGORY_DIVISION_AVAILABLE_CATEGORIES,
  L4_CATEGORY_DIVISION_RESEGMENTED_CATEGORIES,
} from 'lib/networking/endpoints';
import L4SegmentationDetailView from 'components/l4_segmentation/L4SegmentationDetailView';
import L4SegmentationSelector from 'components/l4_segmentation/L4SegmentationSelector';
import { L4_SEGMENTATION_TABS } from 'lib/l4_segmentation/urlSearchParams';
import LargeCategoriesTab from 'components/l4_segmentation/LargeCategoriesTab';
import ResegmentedProductsView from 'components/l4_segmentation/ResegmentedProductsView';
import useGet from 'lib/hooks/useGet';
import useL4SegmentationUrlParams from 'lib/hooks/useL4SegmentationUrlParams';

function L4SegmentationView() {
  const { params, replaceParams } = useL4SegmentationUrlParams();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [resegmentedSelectedCategory, setResegmentedSelectedCategory] = useState(null);

  const segListSkip = params.tab !== L4_SEGMENTATION_TABS.VIEW_RESULTS || !params.l4;
  const { data: segCategoriesPayload } = useGet(
    L4_CATEGORY_DIVISION_AVAILABLE_CATEGORIES,
    {},
    segListSkip,
  );

  const resegListSkip = params.tab !== L4_SEGMENTATION_TABS.VIEW_RESEGMENTED || !params.l4;
  const { data: resegCategoriesPayload } = useGet(
    L4_CATEGORY_DIVISION_RESEGMENTED_CATEGORIES,
    {},
    resegListSkip,
  );

  useEffect(() => {
    if (params.tab !== L4_SEGMENTATION_TABS.VIEW_RESULTS || !params.l4) {
      setSelectedCategory(null);
      return;
    }
    const list = segCategoriesPayload?.data;
    if (!list) return;
    const found = list.find(c => c.id === params.l4);
    if (found) {
      setSelectedCategory(found);
    } else {
      setSelectedCategory(null);
      replaceParams({
        l4: null,
        attr: null,
        cluster: null,
        q: null,
        brand: null,
        entity: null,
        value: null,
        page: null,
        perPage: null,
      });
    }
  }, [params.tab, params.l4, segCategoriesPayload, replaceParams]);

  useEffect(() => {
    if (params.tab !== L4_SEGMENTATION_TABS.VIEW_RESEGMENTED || !params.l4) {
      setResegmentedSelectedCategory(null);
      return;
    }
    const list = resegCategoriesPayload?.data;
    if (!list) return;
    const found = list.find(c => c.id === params.l4);
    if (found) {
      setResegmentedSelectedCategory(found);
    } else {
      setResegmentedSelectedCategory(null);
      replaceParams({
        l4: null,
        cluster: null,
        value: null,
        page: null,
        perPage: null,
      });
    }
  }, [params.tab, params.l4, resegCategoriesPayload, replaceParams]);

  const handleTabSelect = tab => {
    replaceParams({
      tab,
      l4: null,
      attr: null,
      cluster: null,
      q: null,
      brand: null,
      entity: null,
      value: null,
      page: null,
      perPage: null,
    });
    setSelectedCategory(null);
    setResegmentedSelectedCategory(null);
  };

  const handleSelectSegmentationCategory = category => {
    setSelectedCategory(category);
    replaceParams({
      tab: L4_SEGMENTATION_TABS.VIEW_RESULTS,
      l4: category.id,
      attr: null,
      cluster: null,
      q: null,
      brand: null,
      entity: null,
      value: null,
      page: null,
      perPage: null,
    });
  };

  const handleSelectResegmentedCategory = category => {
    setResegmentedSelectedCategory(category);
    replaceParams({
      tab: L4_SEGMENTATION_TABS.VIEW_RESEGMENTED,
      l4: category.id,
      attr: null,
      cluster: null,
      q: null,
      brand: null,
      entity: null,
      value: null,
      page: null,
      perPage: null,
    });
  };

  const handleBackSegmentation = () => {
    setSelectedCategory(null);
    replaceParams({
      l4: null,
      attr: null,
      q: null,
      brand: null,
      entity: null,
      value: null,
      page: null,
      perPage: null,
    });
  };

  const handleBackResegmented = () => {
    setResegmentedSelectedCategory(null);
    replaceParams({
      l4: null,
      cluster: null,
      q: null,
      brand: null,
      entity: null,
      page: null,
      perPage: null,
    });
  };

  return (
    <div>
      <div className={css(styles.pageHeader)}>
        <h3 className={css(styles.pageTitle)}>L4 Segmentation</h3>
      </div>

      <Container className={classNames(css(styles.container), 'mx-5')}>
        <Tabs activeKey={params.tab} onSelect={handleTabSelect} className={css(styles.tabs)}>
          <Tab eventKey={L4_SEGMENTATION_TABS.VIEW_RESULTS} title="View Segmentation Results">
            <div className={css(styles.tabContent)}>
              {!selectedCategory ? (
                <L4SegmentationSelector
                  onSelectCategory={handleSelectSegmentationCategory}
                  endpoint={L4_CATEGORY_DIVISION_AVAILABLE_CATEGORIES}
                />
              ) : (
                <L4SegmentationDetailView
                  category={selectedCategory}
                  onBack={handleBackSegmentation}
                />
              )}
            </div>
          </Tab>
          <Tab
            eventKey={L4_SEGMENTATION_TABS.VIEW_RESEGMENTED}
            title="View Re-segmentation Results"
          >
            <div className={css(styles.tabContent)}>
              {!resegmentedSelectedCategory ? (
                <L4SegmentationSelector
                  onSelectCategory={handleSelectResegmentedCategory}
                  endpoint={L4_CATEGORY_DIVISION_RESEGMENTED_CATEGORIES}
                />
              ) : (
                <ResegmentedProductsView
                  category={resegmentedSelectedCategory}
                  onBack={handleBackResegmented}
                />
              )}
            </div>
          </Tab>
          <Tab eventKey={L4_SEGMENTATION_TABS.SEND_TO_ML} title="Send to AI/ML">
            <div className={css(styles.tabContent)}>
              <LargeCategoriesTab />
            </div>
          </Tab>
        </Tabs>
      </Container>
    </div>
  );
}

const styles = StyleSheet.create({
  pageHeader: {
    background: `linear-gradient(135deg, ${blackberry} 0%, ${purple1} 100%)`,
    padding: '24px 40px',
    marginBottom: '16px',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: 600,
    color: white,
    margin: 0,
  },
  container: {
    minHeight: 'calc(100vh - 150px)',
  },
  tabs: {
    marginBottom: '20px',
  },
  tabContent: {
    minHeight: 'calc(100vh - 250px)',
    paddingTop: '16px',
  },
});

export default L4SegmentationView;
